import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Gavel, 
  Eye, 
  RefreshCw, 
  Trophy, 
  Users, 
  Clock, 
  DollarSign, 
  X, 
  Flame, 
  Search,
  Download,
  Phone,
  Mail,
  Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../config/api';
import { useLogo } from '../../contexts/LogoContext';
import FileUpload from '../common/FileUpload';
import WinnerRevealModal from '../auction/WinnerRevealModal';

const AuctionManagement = () => {
  const { logoUrl } = useLogo();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'completed', 'upcoming'
  const [searchQuery, setSearchQuery] = useState('');
  const [activeViewTab, setActiveViewTab] = useState('items'); // 'items' or 'bids'
  const [bidsSearchQuery, setBidsSearchQuery] = useState('');

  // Modals
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingBidsItem, setViewingBidsItem] = useState(null);
  const [winnerModalItem, setWinnerModalItem] = useState(null);
  const [previewRevealItem, setPreviewRevealItem] = useState(null);
  const [winnerForm, setWinnerForm] = useState({
    devoteeName: '',
    amount: '',
    contact: '',
    note: ''
  });

  // Form State
  // Form State
  const initialForm = {
    title: '',
    category: 'Sacred Item',
    displayOrder: 1,
    description: '',
    blessingsInfo: '',
    startingBid: 101,
    minIncrement: 15,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    images: [],
    status: 'active'
  };
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    fetchAuctions();
  }, []);

  const getAuctionItemRank = (item) => {
    if (!item) return 99;
    if (item.displayOrder !== undefined && item.displayOrder !== null && item.displayOrder !== '') {
      const n = Number(item.displayOrder);
      if (!isNaN(n)) return n;
    }
    if (item.rank !== undefined && item.rank !== null && item.rank !== '') {
      const n = Number(item.rank);
      if (!isNaN(n)) return n;
    }
    try {
      const saved = JSON.parse(localStorage.getItem('bow_auction_ranks') || '{}');
      if (saved[item.id] !== undefined) {
        const n = Number(saved[item.id]);
        if (!isNaN(n)) return n;
      }
    } catch (e) {}

    const title = (item.title || '');
    const numMatch = title.match(/#(\d+)/);
    if (numMatch && numMatch[1]) {
      return Number(numMatch[1]);
    }

    const titleLower = title.toLowerCase();
    const catLower = (item.category || '').toLowerCase();
    if (titleLower.includes('baby pink') || catLower.includes('baby pink') || titleLower.includes('cotton')) {
      return 10;
    }
    if (titleLower.includes('laddu') || catLower.includes('laddu')) {
      return 20;
    }
    return 50;
  };

  const fetchAuctions = async () => {
    try {
      setLoading(true);
      const res = await api.get('/auctions');
      if (res.ok) {
        const json = await res.json();
        const items = (json.data || []).map(item => ({
          ...item,
          displayOrder: getAuctionItemRank(item)
        })).sort((a, b) => a.displayOrder - b.displayOrder);
        setAuctions(items);
      } else {
        toast.error('Failed to load auction items');
      }
    } catch (err) {
      console.error('Error fetching auctions:', err);
      toast.error('Could not connect to auction server');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickRankChange = async (itemId, newRank) => {
    const parsed = parseInt(newRank, 10);
    if (isNaN(parsed) || parsed < 1) return;
    try {
      // 1. Save in localStorage so it takes immediate effect everywhere
      const saved = JSON.parse(localStorage.getItem('bow_auction_ranks') || '{}');
      saved[itemId] = parsed;
      localStorage.setItem('bow_auction_ranks', JSON.stringify(saved));

      // 2. Update React state immediately and re-sort
      setAuctions(prev => {
        const updated = prev.map(a => a.id === itemId ? { ...a, displayOrder: parsed } : a);
        return updated.sort((a, b) => (Number(a.displayOrder) || 99) - (Number(b.displayOrder) || 99));
      });

      // 3. Persist to API
      await api.put(`/auctions/${itemId}`, { displayOrder: parsed, rank: parsed });
      toast.success(`Display rank updated to #${parsed}!`);
    } catch (err) {
      toast.error('Error updating rank');
    }
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({
      ...initialForm,
      displayOrder: auctions.length + 1
    });
    setShowItemModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    const rawImgs = Array.isArray(item.images) ? item.images : (item.images ? [item.images] : []);
    const cleanImages = rawImgs.map(img => typeof img === 'string' ? img : (img?.fileUrl || img?.url || img?.imageUrl)).filter(Boolean);
    const currentRank = getAuctionItemRank(item);
    setFormData({
      title: item.title || '',
      category: item.category || 'Sacred Item',
      displayOrder: currentRank,
      description: item.description || '',
      blessingsInfo: item.blessingsInfo || '',
      startingBid: item.startingBid || 0,
      minIncrement: item.minIncrement || 10,
      startDate: item.startDate ? item.startDate.split('T')[0] : '',
      endDate: item.endDate ? item.endDate.split('T')[0] : '',
      images: cleanImages,
      status: item.status || 'active'
    });
    setShowItemModal(true);
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      setSaving(true);
      const parsedRank = formData.displayOrder !== '' ? Number(formData.displayOrder) : 99;

      if (editingItem) {
        const saved = JSON.parse(localStorage.getItem('bow_auction_ranks') || '{}');
        saved[editingItem.id] = parsedRank;
        localStorage.setItem('bow_auction_ranks', JSON.stringify(saved));
      }

      const payload = {
        ...formData,
        displayOrder: parsedRank,
        rank: parsedRank
      };
      let res;
      if (editingItem) {
        res = await api.put(`/auctions/${editingItem.id}`, payload);
      } else {
        res = await api.post('/auctions', payload);
      }

      if (res.ok) {
        toast.success(editingItem ? 'Auction item updated!' : 'New auction item created!');
        setShowItemModal(false);
        fetchAuctions();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Failed to save auction');
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Error saving auction item');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete auction item "${title}"? This cannot be undone.`)) {
      return;
    }

    try {
      const res = await api.delete(`/auctions/${id}`);
      if (res.ok) {
        toast.success('Auction item deleted');
        setAuctions(prev => prev.filter(a => a.id !== id));
      } else {
        toast.error('Failed to delete auction item');
      }
    } catch (err) {
      toast.error('Error deleting auction item');
    }
  };

  const handleDeleteBid = async (auctionId, bidId) => {
    if (!window.confirm('Delete this bid? The highest bid will be automatically recalculated.')) {
      return;
    }

    try {
      const res = await api.delete(`/auctions/${auctionId}/bids/${bidId}`);
      if (res.ok) {
        const updated = await res.json();
        toast.success('Bid removed and leading amount recalculated!');
        setAuctions(prev => prev.map(a => a.id === auctionId ? updated.data : a));
        setViewingBidsItem(updated.data);
      } else {
        toast.error('Failed to delete bid');
      }
    } catch (err) {
      toast.error('Error deleting bid');
    }
  };

  const handleOpenDeclareWinner = (auction) => {
    const topBid = (auction.bids && auction.bids.length > 0)
      ? [...auction.bids].sort((a, b) => b.amount - a.amount)[0]
      : null;

    setWinnerModalItem(auction);
    setWinnerForm({
      devoteeName: topBid ? topBid.devoteeName : (auction.currentHighestBidder || ''),
      amount: topBid ? topBid.amount : auction.currentHighestBid,
      contact: topBid ? (topBid.phone || topBid.email) : (auction.currentHighestBidderContact || ''),
      note: 'Blessed recipient of divine offerings. Felicitated during the grand concluding ceremony.'
    });
  };

  const handleConfirmDeclareWinner = async (e) => {
    e.preventDefault();
    if (!winnerModalItem) return;

    if (!winnerForm.devoteeName.trim()) {
      toast.error('Devotee name is required');
      return;
    }

    try {
      const res = await api.post(`/auctions/${winnerModalItem.id}/declare-winner`, {
        devoteeName: winnerForm.devoteeName.trim(),
        amount: Number(winnerForm.amount) || winnerModalItem.currentHighestBid,
        contact: winnerForm.contact.trim(),
        note: winnerForm.note.trim()
      });

      if (res.ok) {
        toast.success('🎉 Blessed winner officially declared! Auction concluded.');
        setWinnerModalItem(null);
        fetchAuctions();
      } else {
        toast.error('Failed to declare winner');
      }
    } catch (err) {
      toast.error('Error declaring winner');
    }
  };

  const handleReopenAuction = async (auction) => {
    if (!window.confirm(`Re-open bidding for "${auction.title}"? Status will return to active.`)) {
      return;
    }

    try {
      const res = await api.put(`/auctions/${auction.id}`, {
        status: 'active',
        winner: null
      });

      if (res.ok) {
        toast.success('Auction re-opened for live bidding!');
        fetchAuctions();
      } else {
        toast.error('Failed to reopen auction');
      }
    } catch (err) {
      toast.error('Error reopening auction');
    }
  };

  const handleResetBids = async (auction) => {
    if (!window.confirm(`Reset all bids for "${auction.title}"? This clears bids history and sets current bid back to $${auction.startingBid}.`)) {
      return;
    }

    try {
      const res = await api.post(`/auctions/${auction.id}/reset`);
      if (res.ok) {
        toast.success('All bids cleared! Auction reset to start price.');
        fetchAuctions();
      } else {
        toast.error('Failed to reset bids');
      }
    } catch (err) {
      toast.error('Error resetting bids');
    }
  };

  const handleImageUpload = (fileData) => {
    const url = typeof fileData === 'string'
      ? fileData
      : (fileData?.fileUrl || fileData?.url || fileData?.imageUrl || '');
    if (url) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, url]
      }));
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const allBidsList = useMemo(() => {
    const list = [];
    auctions.forEach(item => {
      (item.bids || []).forEach(bid => {
        list.push({
          ...bid,
          itemTitle: item.title,
          itemId: item.id,
          itemCategory: item.category,
          isLeading: item.currentHighestBid === bid.amount
        });
      });
    });
    return list.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [auctions]);

  const filteredBidsList = useMemo(() => {
    if (!bidsSearchQuery.trim()) return allBidsList;
    const q = bidsSearchQuery.toLowerCase();
    return allBidsList.filter(b => 
      b.devoteeName?.toLowerCase().includes(q) ||
      b.phone?.toLowerCase().includes(q) ||
      b.email?.toLowerCase().includes(q) ||
      b.prayerNote?.toLowerCase().includes(q) ||
      b.itemTitle?.toLowerCase().includes(q)
    );
  }, [allBidsList, bidsSearchQuery]);

  const stats = useMemo(() => {
    const totalItems = auctions.length;
    const totalBids = allBidsList.length;
    const activeItems = auctions.filter(a => a.status === 'active').length;
    const completedItems = auctions.filter(a => a.status === 'completed').length;
    const totalValue = auctions.reduce((acc, curr) => acc + (Number(curr.currentHighestBid) || 0), 0);
    return { totalItems, totalBids, activeItems, completedItems, totalValue };
  }, [auctions, allBidsList]);

  const handleExportCSV = () => {
    if (allBidsList.length === 0) {
      toast.error('No bids to export yet');
      return;
    }
    const headers = ['Sacred Offering', 'Devotee Name', 'Amount ($)', 'Phone', 'Email', 'Prayer Note / Sankalp', 'Anonymous On Public Board', 'Date & Time'];
    const rows = allBidsList.map(b => [
      `"${(b.itemTitle || '').replace(/"/g, '""')}"`,
      `"${(b.devoteeName || '').replace(/"/g, '""')}"`,
      b.amount,
      `"${b.phone || ''}"`,
      `"${b.email || ''}"`,
      `"${(b.prayerNote || '').replace(/"/g, '""')}"`,
      b.isAnonymous ? 'Yes' : 'No',
      `"${new Date(b.timestamp).toLocaleString()}"`
    ]);
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `BOW_Auction_Devotee_Bids_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${allBidsList.length} devotee bids to CSV!`);
  };

  const filteredAuctions = auctions
    .filter(item => {
      const matchesFilter = filter === 'all' || item.status === filter;
      const matchesSearch = item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            item.category?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    })
    .sort((a, b) => (Number(a.displayOrder ?? a.rank) || 99) - (Number(b.displayOrder ?? b.rank) || 99));

  return (
    <div className="space-y-6">
      {/* Header — Donation / Inquiries / Feedback Signature Theme */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-purple-950 to-orange-950 p-6 sm:p-8 text-white shadow-2xl border border-white/10">
        {/* Purple glow in corner matching Inquiries & Feedback */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-purple-600/25 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-600/20 rounded-full blur-2xl pointer-events-none -ml-20 -mb-20" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs font-semibold text-orange-200">
              <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
              <span>Sacred Bidding Management</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2.5">
              <Gavel className="w-7 h-7 text-amber-400" />
              Auctions & Bidding Portal
            </h1>
            <p className="text-sm text-gray-300 max-w-xl">
              Create and manage sacred temple auctions (Maa Durga's Saree, Sacred Laddu, Mahaprasad). Set start prices, min increments, monitor live devotee bids, and declare winners.
            </p>
          </div>

          {/* Action / Refresh with Circular BOW Logo */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleOpenCreate}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-xs sm:text-sm font-bold flex items-center gap-2 shadow-lg transition-all hover:scale-105 active:scale-95"
            >
              <Plus className="w-4 h-4" /> Add Auction Item
            </button>

            {/* Circular BOW Logo Refresh Button */}
            <button
              type="button"
              onClick={fetchAuctions}
              disabled={loading}
              title="Click logo to refresh auctions"
              className="relative z-10 group flex flex-col items-center gap-1.5 focus:outline-none shrink-0 cursor-pointer"
              aria-label="Refresh auctions"
            >
              <div className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden ring-2 ring-white/30 shadow-2xl group-hover:ring-amber-300 group-hover:shadow-[0_0_25px_rgba(251,191,36,0.5)] transition-all duration-300 transform group-hover:scale-110 active:scale-95 bg-white/10 backdrop-blur-md flex items-center justify-center">
                <img
                  src={logoUrl || '/logo512.png'}
                  alt="BOW Logo Refresh"
                  className={`w-full h-full object-cover rounded-full ${loading ? 'animate-spin' : 'group-hover:rotate-12'} transition-transform duration-300`}
                  onError={(e) => {
                    e.currentTarget.src = '/logo512.png';
                  }}
                />
              </div>
              <span className="absolute -bottom-5 text-[10px] text-purple-200 opacity-0 group-hover:opacity-100 transition-opacity font-medium whitespace-nowrap">
                Click to Refresh
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Summary KPI Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center font-bold">
            <Flame className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">Sacred Offerings</span>
            <span className="text-xl font-black text-gray-900">{stats.totalItems}</span>
            <span className="text-[11px] text-emerald-600 font-semibold block">{stats.activeItems} Active</span>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3.5 ring-2 ring-orange-500/20">
          <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center font-bold">
            <Users className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">Devotee Applications</span>
            <span className="text-xl font-black text-orange-600">{stats.totalBids}</span>
            <span className="text-[11px] text-gray-400 font-medium block">Total Bids Placed</span>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold">
            <DollarSign className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">Leading Bids Value</span>
            <span className="text-xl font-black text-gray-900">${stats.totalValue}</span>
            <span className="text-[11px] text-emerald-600 font-semibold block">Community Funds</span>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-yellow-100 text-yellow-600 flex items-center justify-center font-bold">
            <Trophy className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block">Blessed Winners</span>
            <span className="text-xl font-black text-gray-900">{stats.completedItems}</span>
            <span className="text-[11px] text-amber-700 font-semibold block">Declared Recipients</span>
          </div>
        </div>
      </div>

      {/* Main Tab Navigation: Offerings vs Devotee Applications */}
      <div className="border-b border-gray-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setActiveViewTab('items')}
            className={`pb-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-all ${
              activeViewTab === 'items'
                ? 'border-orange-600 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <Flame className="w-4 h-4" /> Sacred Offerings ({auctions.length})
          </button>
          <button
            onClick={() => setActiveViewTab('bids')}
            className={`pb-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-all ${
              activeViewTab === 'bids'
                ? 'border-orange-600 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <Users className="w-4 h-4" /> All Devotee Bids & Contact Info ({allBidsList.length})
          </button>
        </div>
      </div>

      {/* VIEW TAB 1: OFFERINGS GRID */}
      {activeViewTab === 'items' && (
        <div className="space-y-6">
          {/* Filters & Search Bar */}
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
              {['all', 'active', 'upcoming', 'completed'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${
                    filter === status
                      ? 'bg-orange-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>

            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search auctions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          {/* Auction Items Grid */}
          {loading ? (
            <div className="py-20 text-center text-gray-500">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-orange-600" />
              <p>Loading auctions...</p>
            </div>
          ) : filteredAuctions.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-dashed border-gray-300">
              <Gavel className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-800">No auction items found</h3>
              <p className="text-gray-500 text-sm mt-1 max-w-sm mx-auto">
                {searchQuery ? 'No auctions matched your search query.' : 'Create your first sacred auction item to get started.'}
              </p>
              <button
                onClick={handleOpenCreate}
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-xl text-sm font-medium inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Create Auction
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredAuctions.map((auction) => {
                const hasWinner = auction.winner && auction.status === 'completed';
                const rawImg = auction.images && auction.images[0];
                const imageUrl = (typeof rawImg === 'string' ? rawImg : (rawImg?.fileUrl || rawImg?.url || rawImg?.imageUrl)) || (auction.category?.includes('Saree') ? '/maa_durga_no_third_eye.jpg' : '/sacred_maha_laddu_prasad.jpg');

                return (
                  <div 
                    key={auction.id}
                    className="bg-white rounded-2xl border border-gray-200/80 shadow-md hover:shadow-xl transition-all overflow-hidden flex flex-col"
                  >
                    {/* Card Top: Image & Status Badges */}
                    <div className="relative h-48 bg-gray-100 overflow-hidden">
                      <img 
                        src={imageUrl} 
                        alt={auction.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = auction.category?.includes('Saree') 
                            ? '/maa_durga_no_third_eye.jpg' 
                            : '/sacred_maha_laddu_prasad.jpg';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      
                      {/* Status & Rank Badges */}
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <span className="bg-amber-500 text-white font-extrabold text-xs px-2.5 py-1 rounded-full shadow-md backdrop-blur-md border border-amber-300/40 flex items-center gap-1">
                          🏆 Rank #{auction.displayOrder ?? auction.rank ?? '-'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-md backdrop-blur-md ${
                          auction.status === 'active' 
                            ? 'bg-emerald-500 text-white' 
                            : auction.status === 'completed'
                            ? 'bg-amber-500 text-white'
                            : 'bg-blue-500 text-white'
                        }`}>
                          {auction.status}
                        </span>
                        <span className="bg-black/60 backdrop-blur-md text-amber-200 px-2.5 py-1 rounded-full text-xs font-medium">
                          {auction.category}
                        </span>
                      </div>

                      {/* Actions Dropdown / Quick buttons */}
                      <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-md p-1 rounded-xl">
                        <button
                          onClick={() => handleOpenEdit(auction)}
                          className="p-1.5 text-white/90 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                          title="Edit Item"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteItem(auction.id, auction.title)}
                          className="p-1.5 text-red-300 hover:text-red-100 hover:bg-red-500/30 rounded-lg transition-colors"
                          title="Delete Item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Title overlay */}
                      <div className="absolute bottom-3 left-3 right-3 text-white">
                        <h3 className="text-lg font-bold truncate drop-shadow-md">{auction.title}</h3>
                        <p className="text-xs text-amber-200 flex items-center gap-1 mt-0.5">
                          <Clock className="w-3.5 h-3.5" /> Closes: {new Date(auction.endDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {/* Card Body: Bid Stats */}
                    <div className="p-5 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="grid grid-cols-3 gap-2 bg-amber-50/60 rounded-xl p-3 border border-amber-100 mb-4 text-center">
                          <div>
                            <div className="text-[11px] font-medium text-amber-800 uppercase tracking-wider">Start Bid</div>
                            <div className="text-base font-bold text-gray-800">${auction.startingBid}</div>
                          </div>
                          <div className="border-x border-amber-200/60">
                            <div className="text-[11px] font-medium text-orange-800 uppercase tracking-wider">Current Highest</div>
                            <div className="text-lg font-extrabold text-orange-600">${auction.currentHighestBid}</div>
                          </div>
                          <div>
                            <div className="text-[11px] font-medium text-amber-800 uppercase tracking-wider">Min Step</div>
                            <div className="text-base font-bold text-gray-800">+${auction.minIncrement}</div>
                          </div>
                        </div>

                        {/* Leading Bidder or Winner Banner */}
                        {hasWinner ? (
                          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-yellow-300 rounded-2xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 shadow-sm">
                            <div className="flex items-center gap-3">
                              <Trophy className="w-7 h-7 text-yellow-600 flex-shrink-0" />
                              <div className="text-xs">
                                <div className="font-extrabold text-yellow-950 uppercase tracking-wider">🔒 Sealed Winner Ready</div>
                                <div className="text-sm font-black text-orange-600">{auction.winner.devoteeName} (${auction.winner.amount})</div>
                                {auction.winner.contact && <div className="text-xs text-yellow-800">{auction.winner.contact}</div>}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPreviewRevealItem(auction)}
                              className="bg-gradient-to-r from-red-600 via-orange-600 to-amber-600 hover:from-red-700 hover:to-orange-700 text-white font-black text-xs sm:text-sm px-4 py-2.5 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 cursor-pointer transform active:scale-95 shrink-0 border border-amber-300/40"
                            >
                              <Sparkles className="w-4 h-4 text-yellow-300" />
                              Launch Winner Reveal 🏆
                            </button>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-600 mb-4 flex items-center justify-between bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                            <span className="flex items-center gap-1.5 font-medium">
                              <Users className="w-4 h-4 text-orange-500" /> Leading Bidder:
                            </span>
                            <span className="font-bold text-gray-900 truncate max-w-[180px]">
                              {auction.currentHighestBidder || 'No bids yet'}
                            </span>
                          </div>
                        )}

                        <p className="text-xs text-gray-500 line-clamp-2 mb-4">
                          {auction.description || auction.blessingsInfo}
                        </p>
                      </div>

                      {/* Actions Bar */}
                      <div className="pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setViewingBidsItem(auction)}
                            className="bg-orange-50 hover:bg-orange-100 text-orange-700 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" /> View Bids ({auction.bids?.length || 0})
                          </button>
                          <button
                            onClick={() => setPreviewRevealItem(auction)}
                            className="bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-200 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                            title="Preview TV reveal animation (Admin only)"
                          >
                            <Sparkles className="w-3.5 h-3.5 text-amber-600" /> Preview Reveal
                          </button>
                        </div>

                        <div className="flex items-center gap-1.5">
                          {/* Quick Rank Position Input */}
                          <div className="flex items-center gap-1 bg-amber-50/80 border border-amber-300 px-2 py-1 rounded-lg" title="Card Rank (1 = First, 2 = Second...)">
                            <span className="text-[10px] font-extrabold text-amber-900 uppercase">Rank</span>
                            <input
                              type="number"
                              min="1"
                              key={`rank-${auction.id}-${auction.displayOrder}`}
                              defaultValue={auction.displayOrder ?? auction.rank ?? 1}
                              className="w-10 text-center text-xs font-black text-amber-950 bg-white border border-amber-300 rounded px-1 py-0.5 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                              onBlur={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val !== auction.displayOrder) {
                                  handleQuickRankChange(auction.id, val);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.currentTarget.blur();
                              }}
                            />
                          </div>

                          <button
                            onClick={() => handleResetBids(auction)}
                            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 p-1.5 rounded-lg text-xs"
                            title="Reset all bids for fresh start"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>

                          {auction.status !== 'completed' ? (
                            <button
                              onClick={() => handleOpenDeclareWinner(auction)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors shadow-sm"
                            >
                              <Trophy className="w-3.5 h-3.5" /> Declare Winner
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReopenAuction(auction)}
                              className="bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                              title="Re-open auction to resume live bidding"
                            >
                              <RefreshCw className="w-3.5 h-3.5" /> Re-open Auction
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* VIEW TAB 2: DEVOTEE BIDS & PARTICIPANTS TABLE */}
      {activeViewTab === 'bids' && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-gray-100">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-600" />
                All Devotee Applications & Bids
              </h2>
              <p className="text-xs text-gray-500">
                Complete log of every devotee who bid with contact numbers, emails, and prayer notes.
              </p>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search devotee, phone, email..."
                  value={bidsSearchQuery}
                  onChange={(e) => setBidsSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <button
                onClick={handleExportCSV}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-colors whitespace-nowrap"
                title="Download CSV spreadsheet"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>
          </div>

          {filteredBidsList.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <h3 className="font-semibold text-gray-700">No Bids Found</h3>
              <p className="text-xs text-gray-500 mt-1">
                {bidsSearchQuery ? 'No bids matched your search query.' : 'Devotee bids will appear here once placed on the auction page.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 text-gray-600 font-bold uppercase tracking-wider border-y border-gray-200">
                  <tr>
                    <th className="py-3 px-4">#</th>
                    <th className="py-3 px-4">Devotee / Family</th>
                    <th className="py-3 px-4">Sacred Offering</th>
                    <th className="py-3 px-4">Bid Amount</th>
                    <th className="py-3 px-4">Contact Info</th>
                    <th className="py-3 px-4">Sankalp / Prayer Note</th>
                    <th className="py-3 px-4">Date & Time</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredBidsList.map((bid, index) => (
                    <tr key={bid.id || index} className="hover:bg-amber-50/50 transition-colors">
                      <td className="py-3.5 px-4 font-bold text-gray-400">
                        {index + 1}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-gray-900 text-sm">
                          {bid.devoteeName}
                        </div>
                        {bid.isAnonymous && (
                          <span className="inline-block mt-0.5 bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-medium">
                            Anonymous on board
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="font-semibold text-gray-800 block">{bid.itemTitle}</span>
                        <span className="text-[10px] text-gray-500">{bid.itemCategory}</span>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="text-base font-black text-orange-600 block">
                          ${bid.amount}
                        </span>
                        {bid.isLeading && (
                          <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider">
                            <Trophy className="w-2.5 h-2.5" /> Leading
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 space-y-1">
                        {bid.phone && (
                          <a 
                            href={`tel:${bid.phone}`} 
                            className="flex items-center gap-1 text-blue-600 hover:underline font-medium"
                          >
                            <Phone className="w-3 h-3" /> {bid.phone}
                          </a>
                        )}
                        {bid.email && (
                          <a 
                            href={`mailto:${bid.email}`} 
                            className="flex items-center gap-1 text-gray-600 hover:text-blue-600 truncate max-w-[160px]"
                          >
                            <Mail className="w-3 h-3" /> {bid.email}
                          </a>
                        )}
                      </td>
                      <td className="py-3.5 px-4 max-w-xs">
                        {bid.prayerNote ? (
                          <span className="italic text-amber-800 bg-amber-50/80 p-1.5 rounded-lg block border border-amber-100/60 text-[11px]">
                            🙏 "{bid.prayerNote}"
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-gray-500 text-[11px] whitespace-nowrap">
                        {new Date(bid.timestamp).toLocaleDateString()}<br />
                        <span className="text-gray-400">{new Date(bid.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <button
                          onClick={() => handleDeleteBid(bid.itemId, bid.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete bid"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CREATE / EDIT ITEM MODAL */}
      {showItemModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Gavel className="w-5 h-5 text-orange-600" />
                {editingItem ? 'Edit Auction Item' : 'Create New Sacred Auction Item'}
              </h3>
              <button 
                onClick={() => setShowItemModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveItem} className="space-y-4 mt-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Item Title *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 🌺 Maa Durga's Sanctified Silk Saree"
                  value={formData.title}
                  onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Sacred Saree, Sacred Laddu"
                    value={formData.category}
                    onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Display Rank #
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="1 = First, 2 = Second..."
                    value={formData.displayOrder ?? ''}
                    onChange={(e) => setFormData(p => ({ ...p, displayOrder: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-amber-300 bg-amber-50/40 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 font-bold"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(p => ({ ...p, status: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="active">Active (Accepting Bids)</option>
                    <option value="upcoming">Upcoming (Preview Only)</option>
                    <option value="completed">Completed / Closed</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Starting Bid ($)
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={formData.startingBid}
                    onChange={(e) => setFormData(p => ({ ...p, startingBid: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Minimum Increment ($)
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={formData.minIncrement}
                    onChange={(e) => setFormData(p => ({ ...p, minIncrement: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData(p => ({ ...p, startDate: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Closing Date
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData(p => ({ ...p, endDate: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Devotional Description
                </label>
                <textarea
                  rows="3"
                  placeholder="Blessed and sanctified during the auspicious puja..."
                  value={formData.description}
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Blessings & Honors Info
                </label>
                <textarea
                  rows="2"
                  placeholder="Winning devotee receives personal Vedic blessings, Prashad, and honors..."
                  value={formData.blessingsInfo}
                  onChange={(e) => setFormData(p => ({ ...p, blessingsInfo: e.target.value }))}
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Photos & Upload */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Item Images
                </label>
                <FileUpload
                  onUpload={handleImageUpload}
                  accept="image/*"
                  folder="auctions"
                  className="mb-2"
                />
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {formData.images.map((img, idx) => {
                      const imgUrl = typeof img === 'string' ? img : (img?.fileUrl || img?.url || img?.imageUrl);
                      return (
                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group">
                          <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(idx)}
                            className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-80 hover:opacity-100 cursor-pointer"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-gray-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowItemModal(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 rounded-xl text-sm font-semibold shadow-md hover:shadow-lg disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingItem ? 'Save Changes' : 'Create Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW BIDS DRAWER / MODAL */}
      {viewingBidsItem && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{viewingBidsItem.title}</h3>
                <p className="text-xs text-gray-500">
                  Total {viewingBidsItem.bids?.length || 0} bids placed • Current Highest: ${viewingBidsItem.currentHighestBid}
                </p>
              </div>
              <button 
                onClick={() => setViewingBidsItem(null)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4">
              {(!viewingBidsItem.bids || viewingBidsItem.bids.length === 0) ? (
                <div className="text-center py-12 text-gray-400 text-sm">
                  No bids have been placed for this item yet.
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {viewingBidsItem.bids.map((bid, index) => (
                    <div key={bid.id || index} className="py-3 flex items-start justify-between gap-4 hover:bg-gray-50 px-2 rounded-lg transition-colors">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 
                            ? 'bg-amber-100 text-amber-800 border border-amber-300' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          #{index + 1}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900 text-sm">
                              {bid.devoteeName}
                            </span>
                            {bid.isAnonymous && (
                              <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded text-[10px]">
                                Anonymous on board
                              </span>
                            )}
                            {index === 0 && (
                              <span className="bg-amber-500 text-white px-2 py-0.5 rounded text-[10px] font-bold">
                                LEADING
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {bid.phone && <span className="mr-3">📞 {bid.phone}</span>}
                            {bid.email && <span>✉️ {bid.email}</span>}
                          </div>
                          {bid.prayerNote && (
                            <div className="text-xs italic text-amber-700 bg-amber-50/80 p-2 rounded-lg mt-1 border border-amber-100/50">
                              🙏 Sankalp: "{bid.prayerNote}"
                            </div>
                          )}
                          <div className="text-[11px] text-gray-400 mt-1">
                            {new Date(bid.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className="text-base font-extrabold text-orange-600">
                          ${bid.amount}
                        </span>
                        <button
                          onClick={() => handleDeleteBid(viewingBidsItem.id, bid.id)}
                          className="text-gray-300 hover:text-red-600 p-1 rounded transition-colors"
                          title="Delete accidental/spam bid"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setViewingBidsItem(null)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DECLARE WINNER MODAL */}
      {winnerModalItem && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 sm:p-8 animate-fade-in border border-amber-200">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700">
                  <Trophy className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Declare Blessed Winner</h3>
                  <p className="text-xs text-gray-500">Conclude auction and award sacred offering</p>
                </div>
              </div>
              <button 
                onClick={() => setWinnerModalItem(null)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="my-4 p-3 bg-amber-50 rounded-2xl border border-amber-200">
              <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider block">Sacred Offering</span>
              <span className="text-sm font-bold text-gray-900">{winnerModalItem.title}</span>
            </div>

            {/* Quick Picker from Top 3 Bids */}
            {winnerModalItem.bids && winnerModalItem.bids.length > 0 && (
              <div className="mb-4">
                <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wider block mb-2">
                  Select From Top Bidders:
                </span>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {winnerModalItem.bids.slice(0, 3).map((b, i) => (
                    <button
                      key={b.id || i}
                      type="button"
                      onClick={() => setWinnerForm({
                        devoteeName: b.devoteeName,
                        amount: b.amount,
                        contact: b.phone || b.email,
                        note: 'Blessed recipient of divine offerings. Felicitated during the grand concluding ceremony.'
                      })}
                      className={`w-full text-left p-2.5 rounded-xl border text-xs flex items-center justify-between transition-all ${
                        winnerForm.devoteeName === b.devoteeName
                          ? 'border-amber-500 bg-amber-50 font-bold'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center text-[10px] font-bold">
                          #{i + 1}
                        </span>
                        <span>{b.devoteeName} {b.isAnonymous ? '(Anonymous)' : ''}</span>
                      </div>
                      <span className="font-extrabold text-orange-600">${b.amount}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleConfirmDeclareWinner} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Winning Devotee / Family Name *
                </label>
                <input
                  type="text"
                  required
                  value={winnerForm.devoteeName}
                  onChange={(e) => setWinnerForm(p => ({ ...p, devoteeName: e.target.value }))}
                  placeholder="e.g. Sharma Family"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Winning Amount ($)
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={winnerForm.amount}
                    onChange={(e) => setWinnerForm(p => ({ ...p, amount: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                    Contact (Phone / Email)
                  </label>
                  <input
                    type="text"
                    value={winnerForm.contact}
                    onChange={(e) => setWinnerForm(p => ({ ...p, contact: e.target.value }))}
                    placeholder="Phone or Email"
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Ceremony / Felicitation Note
                </label>
                <textarea
                  rows="2"
                  value={winnerForm.note}
                  onChange={(e) => setWinnerForm(p => ({ ...p, note: e.target.value }))}
                  placeholder="e.g. Felicitated during Maha Aarti with holy prasad and blessings certificate"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setWinnerModalItem(null)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold px-5 py-2 rounded-xl text-sm shadow-md hover:shadow-lg flex items-center gap-1.5"
                >
                  <Trophy className="w-4 h-4" /> Confirm & Declare Winner
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADMIN PREVIEW 15-SECOND DRAMATIC REVEAL MODAL */}
      <WinnerRevealModal
        isOpen={!!previewRevealItem}
        onClose={() => setPreviewRevealItem(null)}
        auction={previewRevealItem}
      />
    </div>
  );
};

export default AuctionManagement;
